import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { History, Star, StarOff, Trash2, Volume2 } from 'lucide-react';
import { useSpeechSynthesis } from 'react-speech-kit';
import apiService from '../services/apiService';
import MeaningPopup from '../components/MeaningPopup';

const HistoryContainer = styled.div`
  padding: 40px 20px;
  max-width: 1000px;
  margin: 0 auto;
  background: #141414;
  min-height: 100vh;
  color: #e5e5e5;

  @media (max-width: 480px) {
    padding: 24px 12px; /* Reduce padding for small screens */
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 900;
  color: #e50914;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: 1px;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 30px;
`;

const StatItem = styled.div`
  background: #141414;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.5);
  border: 1px solid #222;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #e5e5e5;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #b3b3b3;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 8px; /* Tighter gap on mobile */
  }
`;

const FilterButton = styled.button`
  padding: 0 16px;
  height: 38px;
  border-radius: 19px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$active ? '#e50914' : '#222'};
  color: ${props => props.$active ? 'white' : '#b3b3b3'};
  border: 1px solid ${props => props.$active ? '#e50914' : '#333'};
  cursor: pointer;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#f40612' : '#333'};
    color: white;
  }

  @media (max-width: 480px) {
    padding: 0 12px; /* Standardize padding to 12px */
    font-size: 0.9rem;
    height: 34px;
    
  }
`;

const SearchInput = styled.input`
  padding: 0 16px;
  height: 38px;
  border-radius: 19px;
  box-sizing: border-box;
  border: 1px solid #333;
  outline: none;
  margin-left: auto;
  min-width: 200px;
  background: #222;
  color: #white;
  font-family: inherit;

  &:focus {
    border-color: #e50914;
    background: #181818;
    color: #fff;
  }

  @media (max-width: 480px) {
    margin-left: 0;
    width: 100%; /* Full width search on mobile */
    min-width: 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px;
  color: #718096;
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  color: #cbd5e0;
`;

const WordList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WordItem = styled.div`
  background: #141414;
  border-radius: 12px;
  padding: 16px 24px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.3);
  border: 1px solid #444;       /* Increased visibility: #222 -> #444 */
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;
  cursor: pointer;
  border-left: 4px solid #333; /* Make left border visible (dark grey spine) */

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch; /* Stretch to fill width */
    gap: 12px;
    padding: 16px; /* slightly less padding */
  }

  &:hover {
    transform: translateY(-2px);
    border-color: #666;
    border-left-color: #e50914;
  }
`;

const WordInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column; /* Stack word and meaning */
    align-items: flex-start; /* Left align */
    gap: 8px; /* Gap between word and meaning */
  }
`;

const WordText = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  min-width: 120px;
  @media (max-width: 480px) {
    min-width: auto;
    width: 100%; /* Full width */
    font-size: 1.2rem; /* Slightly larger for readability */
    margin-bottom: 2px;
  }
`;

const WordTranslation = styled.div`
  color: #b3b3b3;
  font-size: 1rem;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WordMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: #666;
  font-size: 0.9rem;

  @media (max-width: 480px) {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap; /* Allow wrapping */
    margin-top: 4px;
    gap: 8px; /* Gap for wrapped items */
  }
`;

const Badge = styled.span`
  background: #222;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #b3b3b3;
  border: 1px solid #333;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;

  @media (max-width: 480px) {
    width: 100%; /* Force new line */
    margin-left: 0;
    margin-top: 8px;
    justify-content: flex-end; /* Align right */
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #666;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #222;
    color: #e5e5e5;
  }

  &.favorite {
    color: #e50914; /* Red star for favorite to match theme? Or Gold? Let's use Gold or Red. Theme is Red. */
    color: #ffd700; 
  }
  
  &.delete:hover {
    color: #e50914;
    background: rgba(229, 9, 20, 0.1);
  }
`;

const LoadingSentinel = styled.div`
  text-align: center;
  padding: 20px;
  color: #a0aec0;
  font-size: 0.9rem;
`;

const SortSelect = styled.select`
  padding: 0 16px;  
  height: 38px;
  /* line-height removed to fix alignment */
  border-radius: 19px;
  box-sizing: border-box;
  background: #222;
  color: #b3b3b3;
  border: 1px solid #333;
  cursor: pointer;
  font-weight: 500;
  font-family: inherit; /* Ensure font matches */
  outline: none;
  
  /* Normalize appearance */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23b3b3b3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px; 

  &:hover, &:focus {
    border-color: #e50914;
    color: white;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  }

  @media (max-width: 480px) {
    padding: 0 24px 0 12px; /* Keep padding for arrow */
    font-size: 0.9rem;
    height: 34px;
    line-height: 32px; /* Force line-height to (Height - Border) for vertical centering */
    background-position: right 4px center;
    text-align: center;
    text-align-last: center;
  }
`;

function WordHistory() {
  const location = useLocation();
  const [words, setWords] = useState([]);
  const [filteredWords, setFilteredWords] = useState([]);
  const [filter, setFilter] = useState('all'); // all, favorites
  const [sortBy, setSortBy] = useState('frequency'); // date, frequency
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  const { speak } = useSpeechSynthesis();

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const observer = useRef();

  // Intersection Observer for Infinite Scroll
  const lastWordElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    // Only observe if we have more data and are in 'all' view without search
    if (hasMore && filter === 'all' && !searchTerm) {
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          setPage(prevPage => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    }
  }, [loading, hasMore, filter, searchTerm]);

  // Popup state
  const [popup, setPopup] = useState(null);
  const [wordInfo, setWordInfo] = useState(null);

  // Read query params for initial filter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    if (filterParam === 'favorites') {
      setFilter('favorites');
    } else {
      setFilter('all');
    }
  }, [location.search]);

  useEffect(() => {
    loadFavorites();
  }, []);

  // Load words when page or filter changes
  useEffect(() => {
    if (filter === 'all' && !searchTerm) {
      loadWordHistory(page, sortBy);
    }
    // eslint-disable-next-line
  }, [page, filter, searchTerm, sortBy]); // Added sortBy dependency

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setWords([]);
    setPage(1);
    setHasMore(true);
  };

  // Handle Favorites Toggle Filter
  useEffect(() => {
    if (filter === 'favorites') {
      loadFavoritesData();
    } else if (filter === 'all' && page === 1 && !searchTerm) {
      // If switching back to 'all', words logic handled by above useEffect
    }
  }, [filter]);

  const loadWordHistory = async (pageNum, sortOption) => {
    try {
      console.log('Loading word history, page:', pageNum, 'sort:', sortOption);
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));

      if (!user || !user.id) {
        console.error('No user found in localStorage');
        return;
      }

      const response = await apiService.getWordHistory(user.id, pageNum, 20, sortOption);

      console.log('API Response:', response);

      if (response.success) {
        const newWords = response.data.map(w => ({
          ...w,
          clickedAt: w.updatedAt ? (w.updatedAt._seconds * 1000) : Date.now()
        }));

        console.log('Mapped Words:', newWords);

        setWords(prev => {
          if (pageNum === 1) return newWords;
          // Append new words, avoiding duplicates by ID
          const existingIds = new Set(prev.map(w => w.id));
          const uniqueNewWords = newWords.filter(w => !existingIds.has(w.id));
          return [...prev, ...uniqueNewWords];
        });

        if (response.pagination) {
          setHasMore(response.pagination.hasMore);
        } else {
          setHasMore(newWords.length === 20);
        }
      } else {
        console.error('API Error:', response);
      }
    } catch (err) {
      console.error('Failed to load word history', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFavoritesData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        const res = await apiService.getFavoriteWords(user.id);
        if (res && res.success) {
          const favs = res.data.map(w => ({
            ...w,
            clickedAt: w.updatedAt ? (w.updatedAt._seconds * 1000) : Date.now()
          }));
          setWords(favs);
          setHasMore(false);

          // Sync local state and storage with server data
          const newFavSet = new Set(favs.map(w => w.id));
          setFavorites(newFavSet);
          localStorage.setItem('favoriteWords', JSON.stringify([...newFavSet]));
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = () => {
    const favs = JSON.parse(localStorage.getItem('favoriteWords') || '[]');
    setFavorites(new Set(favs));
  };

  const handleFilterChange = (newFilter) => {
    // If switching to all, reset pagination
    if (newFilter === 'all') {
      setWords([]);
      setPage(1);
      setHasMore(true);
    } else {
      setWords([]); // Clear for favorites loading
    }
    setFilter(newFilter);
    setSearchTerm(''); // Clear search when switching tabs for simplicity
  };

  // Client-side filtering wrapper (mostly for Search)
  useEffect(() => {
    let result = [...words];

    if (searchTerm) {
      result = result.filter(w => {
        const wordText = w.word || w.text || '';
        const trans = w.translation || '';
        return wordText.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trans.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    setFilteredWords(result);
  }, [words, searchTerm]);


  const toggleFavorite = async (e, wordId) => {
    e.stopPropagation();
    const newFavorites = new Set(favorites);
    const isFav = !newFavorites.has(wordId);

    if (isFav) {
      newFavorites.add(wordId);
    } else {
      newFavorites.delete(wordId);
    }

    setFavorites(newFavorites);
    localStorage.setItem('favoriteWords', JSON.stringify([...newFavorites]));

    try {
      await apiService.saveFavoriteWord(wordId, isFav);
    } catch (err) {
      console.error('Failed to save favorite', err);
    }
  };

  const removeWord = async (e, wordId) => {
    e.stopPropagation();
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    // Optimistic UI update
    const previousWords = words;
    const updatedWords = words.filter(word => word.id !== wordId);
    setWords(updatedWords);

    try {
      const response = await apiService.deleteWord(wordId);
      if (!response.success) {
        throw new Error('Delete failed');
      }
    } catch (err) {
      console.error('Failed to delete word:', err);
      alert('삭제에 실패했습니다.');
      setWords(previousWords); // Revert on failure
    }
  };

  const speakWord = (e, wordObject) => {
    e.stopPropagation();
    const text = wordObject.word || wordObject.text || '';
    if (text) {
      speak({ text, rate: 0.8, pitch: 1 });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleWordClick = (word) => {
    const wordText = word.word || word.text;

    const meanings = (word.meanings && word.meanings.length > 0) ? word.meanings.map(m => ({
      ...m,
      meaning: m.meaning || m.definition || m.의미1 || m.의미2 || m.의미3,
      example: m.example || m.exampleSentence || m.예문1 || m.예문2 || m.예문3
    })) : [{
      meaning: word.translation || '의미 없음',
      example: (typeof word.exampleSentence === 'string' ? word.exampleSentence : word.exampleSentence?.text) || ''
    }];

    setWordInfo({
      word: wordText,
      meanings: meanings,
      id: word.id,
      isFavorite: favorites.has(word.id)
    });

    const exampleText = (meanings[0] && meanings[0].example) ? meanings[0].example : wordText;

    setPopup({
      source: exampleText,
      target: ''
    });
  };

  const handleUpdateWordInfo = (updatedInfo) => {
    setWordInfo(updatedInfo);

    const newFavorites = new Set(favorites);
    if (updatedInfo.isFavorite) {
      newFavorites.add(updatedInfo.id);
    } else {
      newFavorites.delete(updatedInfo.id);
    }
    setFavorites(newFavorites);
    localStorage.setItem('favoriteWords', JSON.stringify([...newFavorites]));
  };

  const stats = {
    favorites: favorites.size,
    // Total/Recent removed from stats bar as we don't have full counts efficiently
  };

  return (
    <HistoryContainer>
      <Title>
        <History size={32} />
        단어 히스토리
      </Title>

      <StatsSection>
        <StatItem>
          <StatNumber>{favorites.size}</StatNumber>
          <StatLabel>즐겨찾기 개수</StatLabel>
        </StatItem>
        {/* Can add more stats if backend provides metadata */}
      </StatsSection>

      <FilterSection>
        <FilterButton
          $active={filter === 'all'}
          onClick={() => handleFilterChange('all')}
        >
          전체
        </FilterButton>
        <FilterButton
          $active={filter === 'favorites'}
          onClick={() => handleFilterChange('favorites')}
        >
          즐겨찾기
        </FilterButton>
        <SortSelect value={sortBy} onChange={handleSortChange}>
          <option value="date">최신순</option>
          <option value="frequency">많이 본 순</option>
        </SortSelect>
        <SearchInput
          type="text"
          placeholder="검색..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FilterSection>

      {filteredWords.length === 0 && !loading ? (
        <EmptyState>
          <EmptyIcon>
            <History size={40} />
          </EmptyIcon>
          <h3>표시할 단어가 없습니다</h3>
        </EmptyState>
      ) : (
        <WordList>
          {filteredWords.map((word, index) => {
            // Attach ref to last element
            const isLast = index === filteredWords.length - 1;
            return (
              <WordItem
                ref={isLast ? lastWordElementRef : null}
                key={word.id}
                onClick={() => handleWordClick(word)}
              >
                <WordInfo>
                  <WordText>{word.word || word.text}</WordText>
                  <WordTranslation>
                    {word.meanings && word.meanings[0] ? (word.meanings[0].meaning || word.meanings[0].의미1) : word.translation}
                  </WordTranslation>
                </WordInfo>
                <WordMeta>
                  <Badge>{word.frequency}회</Badge>
                  <span>{formatDate(word.clickedAt)}</span>
                  <ActionButtons>
                    <ActionButton onClick={(e) => speakWord(e, word)}>
                      <Volume2 size={16} />
                    </ActionButton>
                    <ActionButton
                      className={favorites.has(word.id) ? 'favorite' : ''}
                      onClick={(e) => toggleFavorite(e, word.id)}
                    >
                      {favorites.has(word.id) ? <Star size={16} /> : <StarOff size={16} />}
                    </ActionButton>
                    <ActionButton className="delete" onClick={(e) => removeWord(e, word.id)}>
                      <Trash2 size={16} />
                    </ActionButton>
                  </ActionButtons>
                </WordMeta>
              </WordItem>
            );
          })}
        </WordList>
      )}

      {loading && <LoadingSentinel>불러오는 중...</LoadingSentinel>}

      {popup && (
        <MeaningPopup
          source={popup.source}
          target={popup.target}
          onClose={() => setPopup(null)}
          wordInfo={wordInfo}
          onWordClick={() => { }}
          loadingWord={false}
          onUpdateWordInfo={handleUpdateWordInfo}
        />
      )}
    </HistoryContainer>
  );
}

export default WordHistory;
